import { useState, type PointerEvent, type MouseEvent } from "react";
import { Reply, SquareSlash, X } from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IconButton, ToolbarTooltip } from "./ToolbarButton";
import { SLASH_SNIPPETS } from "./markdown-commands";
import { useSavedReplies } from "./useSavedReplies";

// Marks menu content so MarkdownEditor's blur handler knows focus stayed in
// the editor even though the menu is portaled outside it.
export const POPUP_ATTR = "data-md-popup";

interface MenuProps {
	onInsert: (text: string, options?: { block?: boolean }) => void;
	// Menus return focus to the textarea instead of their trigger.
	onClose: () => void;
}

const contentProps = (onClose: () => void) => ({
	align: "end" as const,
	[POPUP_ATTR]: "",
	onCloseAutoFocus: (e: Event) => {
		e.preventDefault();
		onClose();
	},
});

export function SlashCommandsMenu({ onInsert, onClose }: MenuProps) {
	return (
		<DropdownMenu>
			<ToolbarTooltip label="Slash commands">
				<DropdownMenuTrigger asChild>
					<IconButton icon={SquareSlash} label="Slash commands" />
				</DropdownMenuTrigger>
			</ToolbarTooltip>
			<DropdownMenuContent className="w-48" {...contentProps(onClose)}>
				<DropdownMenuLabel>Slash commands</DropdownMenuLabel>
				{SLASH_SNIPPETS.map((snippet) => (
					<DropdownMenuItem
						key={snippet.label}
						onSelect={() => onInsert(snippet.text, { block: true })}
					>
						{snippet.label}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function stop(e: PointerEvent | MouseEvent) {
	e.preventDefault();
	e.stopPropagation();
}

export function SavedRepliesMenu({
	onInsert,
	onClose,
	getText,
}: MenuProps & { getText: () => string }) {
	const { replies, add, remove } = useSavedReplies();
	// Controlled so opening re-renders and getText() below reads the live text.
	const [open, setOpen] = useState(false);

	return (
		<DropdownMenu open={open} onOpenChange={setOpen}>
			<ToolbarTooltip label="Saved replies">
				<DropdownMenuTrigger asChild>
					<IconButton icon={Reply} label="Saved replies" />
				</DropdownMenuTrigger>
			</ToolbarTooltip>
			<DropdownMenuContent className="w-72" {...contentProps(onClose)}>
				<DropdownMenuLabel>Saved replies</DropdownMenuLabel>
				{replies.length === 0 && (
					<p className="px-2 pb-1.5 text-xs text-muted">
						No saved replies yet.
					</p>
				)}
				{replies.map((reply) => (
					<DropdownMenuItem key={reply} onSelect={() => onInsert(reply)}>
						<span className="flex-1 truncate">{reply}</span>
						{/* Pointer handlers stop Radix from treating the click as item select. */}
						<button
							type="button"
							aria-label="Delete saved reply"
							className="rounded p-0.5 text-muted hover:text-danger"
							onPointerDown={stop}
							onPointerUp={stop}
							onClick={(e) => {
								stop(e);
								remove(reply);
							}}
						>
							<X className="size-3.5" />
						</button>
					</DropdownMenuItem>
				))}
				<DropdownMenuSeparator />
				<DropdownMenuItem
					disabled={!getText().trim()}
					onSelect={() => add(getText())}
				>
					Save current text as reply
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
